import { useEffect, useRef, useState } from "react";
import { Download, Share2, Loader2 } from "lucide-react";
import { EVENT } from "../../data/content";
import { useLang } from "../../i18n/LanguageContext";

const W = 1080;
const H = 1350;

// Draw the celestial shareable card onto a canvas
const drawCard = (ctx, { name, category }) => {
  // background
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, "#05070A");
  g.addColorStop(0.5, "#0A0D17");
  g.addColorStop(1, "#111625");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // gold radial glow
  const rg = ctx.createRadialGradient(W / 2, H * 0.42, 40, W / 2, H * 0.42, 620);
  rg.addColorStop(0, "rgba(217,119,6,0.28)");
  rg.addColorStop(1, "rgba(217,119,6,0)");
  ctx.fillStyle = rg;
  ctx.fillRect(0, 0, W, H);

  // stars
  for (let i = 0; i < 140; i++) {
    const x = Math.random() * W;
    const y = Math.random() * H;
    const r = Math.random() * 2 + 0.4;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = Math.random() > 0.7 ? "rgba(253,230,138,0.9)" : "rgba(255,255,255,0.55)";
    ctx.fill();
  }

  // gold border frame
  ctx.strokeStyle = "rgba(253,230,138,0.5)";
  ctx.lineWidth = 3;
  ctx.strokeRect(48, 48, W - 96, H - 96);

  ctx.textAlign = "center";

  // presenter
  ctx.fillStyle = "#FDE68A";
  ctx.font = "600 26px 'Outfit', sans-serif";
  ctx.fillText("LITTLE SAPIENS · EARLY YEARS PRESENTS", W / 2, 170);

  // Title
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "300 118px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("SAPIENS", W / 2, 320);
  const gt = ctx.createLinearGradient(W / 2 - 320, 0, W / 2 + 320, 0);
  gt.addColorStop(0, "#FDE68A");
  gt.addColorStop(1, "#D97706");
  ctx.fillStyle = gt;
  ctx.font = "600 132px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("CELESTIA", W / 2, 450);

  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.font = "italic 500 56px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("Runway of Dreams", W / 2, 530);

  // divider
  ctx.strokeStyle = "rgba(253,230,138,0.5)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(W / 2 - 120, 590);
  ctx.lineTo(W / 2 + 120, 590);
  ctx.stroke();

  // headline
  ctx.fillStyle = "#FFFFFF";
  ctx.font = "300 60px 'Cormorant Garamond', Georgia, serif";
  ctx.fillText("I'm performing at", W / 2, 720);

  // child name (gold)
  ctx.fillStyle = gt;
  const nm = (name || "Our Star").toUpperCase();
  let fs = 100;
  ctx.font = `700 ${fs}px 'Cormorant Garamond', Georgia, serif`;
  while (ctx.measureText(nm).width > W - 200 && fs > 40) {
    fs -= 4;
    ctx.font = `700 ${fs}px 'Cormorant Garamond', Georgia, serif`;
  }
  ctx.fillText(nm, W / 2, 830);

  // category pill
  if (category) {
    ctx.fillStyle = "rgba(253,230,138,0.9)";
    ctx.font = "500 40px 'Outfit', sans-serif";
    ctx.fillText("★  " + category + "  ★", W / 2, 910);
  }

  // details
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.font = "500 40px 'Outfit', sans-serif";
  ctx.fillText("August 8 – 9, 2026", W / 2, 1030);
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.font = "400 34px 'Outfit', sans-serif";
  ctx.fillText("RGMC Gymkhana Club, Patiala", W / 2, 1085);

  // footer hashtag
  ctx.fillStyle = "#FDE68A";
  ctx.font = "600 34px 'Outfit', sans-serif";
  ctx.fillText(EVENT.hashtag + "   ·   @" + EVENT.instagram, W / 2, 1230);
};

export const ShareableCard = ({ name, category }) => {
  const { t } = useLang();
  const s = t.registration.success;
  const canvasRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [canShare, setCanShare] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const render = async () => {
      try {
        if (document.fonts && document.fonts.load) {
          await Promise.all([
            document.fonts.load("700 100px 'Cormorant Garamond'"),
            document.fonts.load("500 40px 'Outfit'"),
          ]);
        }
      } catch (e) { /* fonts optional */ }
      if (cancelled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      drawCard(ctx, { name, category });
    };
    render();
    setCanShare(typeof navigator !== "undefined" && !!navigator.canShare);
    return () => { cancelled = true; };
  }, [name, category]);

  const getBlob = () =>
    new Promise((resolve) => canvasRef.current.toBlob(resolve, "image/png", 0.95));

  const download = async () => {
    const blob = await getBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sapiens-celestia-card.png";
    a.click();
    URL.revokeObjectURL(url);
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob = await getBlob();
      const file = new File([blob], "sapiens-celestia-card.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Sapiens Celestia 2026",
          text: `${name} is performing at Sapiens Celestia 2026! ${EVENT.hashtag}`,
        });
      } else {
        await download();
      }
    } catch (e) { /* user cancelled */ }
    setBusy(false);
  };

  return (
    <div data-testid="shareable-card">
      <div className="overflow-hidden rounded-2xl border border-gold-light/25">
        <canvas ref={canvasRef} width={W} height={H} className="block h-auto w-full" data-testid="share-canvas" />
      </div>
      <p className="mt-4 text-center text-sm text-white/55">{s.shareHint}</p>
      <div className="mt-4 flex justify-center gap-3">
        <button onClick={download} data-testid="share-download-btn" className="flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-white/85 transition-colors hover:border-gold-light/50 hover:text-gold-light">
          <Download className="h-4 w-4" /> {s.download}
        </button>
        {canShare && (
          <button onClick={share} disabled={busy} data-testid="share-native-btn" className="flex items-center gap-2 rounded-full bg-gradient-to-r from-gold-light to-gold-primary px-6 py-3 text-sm font-medium text-ink-900 disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Share2 className="h-4 w-4" />} {s.shareBtn}
          </button>
        )}
      </div>
    </div>
  );
};
